import React from 'react';
import LabelChip from '../../components/label-chip';
import type {
  SettingsControl,
  SettingsLayout,
  SettingsNode,
  SettingsRow,
} from './types';
import {
  cloneFastFillSettings,
  sourceFieldOptions,
  type FastFillConfiguration,
  type FastFillSettingsDraft,
  type FastFillSettingsEnvironment,
} from './fast-fill-settings-adapter';

let configurationSequence = 0;

export const createEmptyFastFillConfiguration = (): FastFillConfiguration => {
  configurationSequence += 1;
  return {
    id: `fast-fill-configuration-${configurationSequence}`,
    sourceKey: '',
    targetFieldKeys: [],
    type: 'generic',
    isExisting: false,
  };
};

const updateConfiguration = (
  draft: FastFillSettingsDraft,
  configurationId: string,
  update: Partial<FastFillConfiguration>,
): FastFillSettingsDraft => ({
  ...draft,
  configurations: draft.configurations.map((configuration) => (
    configuration.id === configurationId
      ? { ...configuration, ...update }
      : configuration
  )),
});

const hasValidSource = (
  sourceKey: string,
  environment: FastFillSettingsEnvironment,
): boolean => environment.sourceFields.some(({ key }) => key === sourceKey);

const hasAvailableSource = (environment: FastFillSettingsEnvironment): boolean => (
  environment.sourceFields.length > 0
);

const canDeleteConfiguration = (
  configuration: FastFillConfiguration,
  draft: FastFillSettingsDraft,
  environment: FastFillSettingsEnvironment,
): boolean => draft.configurations.length > 1 || hasValidSource(
  configuration.sourceKey,
  environment,
);

const configurationRows = (
  draft: FastFillSettingsDraft,
  environment: FastFillSettingsEnvironment,
): SettingsRow<FastFillSettingsDraft, FastFillSettingsEnvironment>[] => (
  draft.configurations.map((configuration) => {
    const sourceControl: SettingsControl<
    FastFillSettingsDraft,
    FastFillSettingsEnvironment
    > = {
      id: `${configuration.id}-source`,
      kind: 'select',
      typeahead: true,
      hideDisabledOptionIcon: true,
      ariaLabel: 'Source Element',
      placeholder: 'Select Source Element',
      grow: true,
      value: configuration.sourceKey,
      options: sourceFieldOptions(draft, environment, configuration.id),
      onChange: (sourceKey) => updateConfiguration(
        draft,
        configuration.id,
        sourceKey === configuration.sourceKey
          ? { sourceKey }
          : {
            sourceKey,
            targetFieldKeys: [],
            legacyTargetDataNames: [],
            legacyPromptOrder: [],
          },
      ),
    };

    return {
      id: configuration.id,
      controls: [sourceControl],
      actions: [{
        id: `${configuration.id}-targets`,
        label: `Select Fields (${configuration.targetFieldKeys.length + (configuration.legacyTargetDataNames?.length ?? 0)})`,
        disabled: !hasValidSource(configuration.sourceKey, environment),
        opensChild: true,
      }],
      deleteLabel: 'Delete FastFill configuration',
      deleteDisabled: !canDeleteConfiguration(configuration, draft, environment),
      onDelete: () => {
        const configurations = draft.configurations.filter(
          (current) => current.id !== configuration.id,
        );

        return {
          ...draft,
          configurations: configurations.length > 0
            ? configurations
            : [createEmptyFastFillConfiguration()],
        };
      },
    };
  })
);

const fastFillSettingsLayout: SettingsLayout<
FastFillSettingsDraft,
FastFillSettingsEnvironment
> = ({ draft, environment }): SettingsNode<
FastFillSettingsDraft,
FastFillSettingsEnvironment
>[] => [
  {
    kind: 'group',
    id: 'fast-fill-heading',
    spacing: 1,
    children: [
      {
        kind: 'text',
        id: 'fast-fill-title',
        variant: 'body1',
        text: 'Photo FastFill',
        endAdornment: React.createElement(LabelChip, { label: 'Beta' }),
      },
      {
        kind: 'text',
        id: 'fast-fill-description',
        variant: 'subtitle2',
        color: 'text.secondary',
        text: 'Choose a photo field, then select which fields it should automatically fill.',
      },
    ],
  },
  {
    kind: 'repeatable',
    id: 'fast-fill-configurations',
    maxHeight: 335,
    items: configurationRows(draft, environment),
  },
  {
    kind: 'action',
    id: 'add-fast-fill-configuration',
    label: 'Add New',
    icon: 'add',
    disabled: !hasAvailableSource(environment),
    onClick: () => {
      const clonedDraft = cloneFastFillSettings(draft);
      return {
        ...clonedDraft,
        configurations: [
          ...clonedDraft.configurations,
          createEmptyFastFillConfiguration(),
        ],
      };
    },
  },
  {
    kind: 'control',
    control: {
      id: 'audio-fast-fill-enabled',
      kind: 'checkbox',
      label: 'Audio FastFill',
      labelVariant: 'body1',
      description: 'Enabling Audio FastFill will allow mobile users to populate app fields with AI audio dictation.',
      visible: (context) => context.environment.audioFastFillVisible,
      disabled: (context) => context.environment.audioFastFillDisabled,
      containerSx: {
        borderTop: 1,
        borderColor: 'divider',
      },
      labelSx: (theme) => ({
        marginLeft: theme.spacing(-1),
        alignItems: 'flex-start',
        paddingTop: 2,
        width: '100%',
      }),
      value: draft.audioFastFillEnabled,
      onChange: (audioFastFillEnabled) => ({ ...draft, audioFastFillEnabled }),
    },
  },
];

export default fastFillSettingsLayout;
